import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import {
  getCancelledTransactionService,
  getPendingTransactionService,
  getPrimaryTransactionService,
  getSecondaryTransactionService,
  getWithdrawalTransactionService,
  approveTransactions
} from '../../services/transactions.service';

const initialState = {
  transactionsList: {
    totalCount: 0,
    totalPage: 0,
    transactionsData: [],
  },
};

export const getPrimaryTransactions = createAsyncThunk(
  `PRIMARY_TRANSACTIONS`,
  async (
    {
      page,
      limit,
      sortField,
      sortType,
      search,
      userId,
      propertyId,
      filterBy,
      filterValues,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getPrimaryTransactionService(
        page,
        limit,
        sortField,
        sortType,
        search,
        userId,
        propertyId,
        filterBy,
        filterValues
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getSecondaryTransactions = createAsyncThunk(
  `SECONDARY_TRANSACTIONS`,
  async (
    {
      page,
      limit,
      sortField,
      sortType,
      search,
      userId,
      propertyId,
      filterBy,
      filterValues,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getSecondaryTransactionService(
        page,
        limit,
        sortField,
        sortType,
        search,
        userId,
        propertyId,
        filterBy,
        filterValues
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getPendingTransactions = createAsyncThunk(
  `PENDING_TRANSACTIONS`,
  async (
    {
      page,
      limit,
      sortField,
      sortType,
      search,
      userId,
      propertyId,
      filterBy,
      filterValues,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getPendingTransactionService(
        page,
        limit,
        sortField,
        sortType,
        search,
        userId,
        propertyId,
        filterBy,
        filterValues
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getCancelledTransactions = createAsyncThunk(
  `CANCELLED_TRANSACTIONS`,
  async (
    {
      page,
      limit,
      sortField,
      sortType,
      search,
      userId,
      propertyId,
      filterBy,
      filterValues,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getCancelledTransactionService(
        page,
        limit,
        sortField,
        sortType,
        search,
        userId,
        propertyId,
        filterBy,
        filterValues
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getWithdrawalTransactions = createAsyncThunk(
  `WITHDRAWAL_TRANSACTIONS`,
  async (
    {
      page,
      limit,
      sortField,
      sortType,
      search,
      userId,
      propertyId,
      filterBy,
      filterValues,
      isWithdrawalApproved,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getWithdrawalTransactionService(
        page,
        limit,
        sortField,
        sortType,
        search,
        userId,
        propertyId,
        filterBy,
        filterValues,
        isWithdrawalApproved
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const approveTransaction = createAsyncThunk(
  `APPROVE_TRANSACTION`,
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await approveTransactions(transactionId);
      return { ...response, transactionId };
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
)

const transactionsSlice = createSlice({
  name: 'transactionsSlice',
  initialState,
  reducers: {
    resetTransactions: (state) => {
      state.transactionsList = {
        totalCount: 0,
        totalPage: 0,
        transactionsData: [],
      };
    },
  },
  extraReducers: (builder) => {
    // Primary Transaction
    builder
      .addCase(getPrimaryTransactions.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, primaryMarketTransactions } =
            payload?.data?.data;
          state.transactionsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            transactionsData: [...(primaryMarketTransactions ?? [])],
          };
        }
      })
      .addCase(getPrimaryTransactions.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Secondary Transaction
    builder
      .addCase(getSecondaryTransactions.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, secondaryMarketTransactions } =
            payload?.data?.data;
          state.transactionsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            transactionsData: [...(secondaryMarketTransactions ?? [])],
          };
        }
      })
      .addCase(getSecondaryTransactions.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Pending Transactions
    builder
      .addCase(getPendingTransactions.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, pendingOrders } = payload?.data?.data;
          state.transactionsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            transactionsData: [...(pendingOrders ?? [])],
          };
        }
      })
      .addCase(getPendingTransactions.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Cancelled Transactions
    builder
      .addCase(getCancelledTransactions.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, cancelOrders } = payload?.data?.data;
          state.transactionsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            transactionsData: [...(cancelOrders ?? [])],
          };
        }
      })
      .addCase(getCancelledTransactions.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // withdrawal Transactions
    builder
      .addCase(getWithdrawalTransactions.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, withdrawTransfers } = payload?.data?.data;
          state.transactionsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            transactionsData: [...(withdrawTransfers ?? [])],
          };
        }
      })
      .addCase(getWithdrawalTransactions.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Approve Transactions
    builder
      .addCase(approveTransaction.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.transactionsList = {
            ...current(state.transactionsList),
            transactionsData: current(state.transactionsList).transactionsData?.map((transaction) =>
              transaction.transferId === payload.transactionId.id
                ? { ...transaction, isWithdrawalApproved: true }
                : transaction
            )
          }
        }
      })
      .addCase(approveTransaction.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });
  },
});

export const { resetTransactions } = transactionsSlice.actions;

export default transactionsSlice.reducer;
